import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Mail, Phone, MessageSquare, RefreshCcw } from "lucide-react";

const STATUSES = ["New", "Contacted", "Closed"];
const STATUS_STYLE = {
    New: "bg-amber-50 text-amber-800 border-amber-200",
    Contacted: "bg-blue-50 text-blue-800 border-blue-200",
    Closed: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

// Admin / Super-Admin view of public "Send us a Message" enquiries.
// Backend enforces admin-only access; this page just lists and lets the
// operator move status through New → Contacted → Closed.
export default function Enquiries() {
    const [items, setItems] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);

    async function load() {
        setBusy(true);
        try {
            const params = statusFilter !== "all" ? { status: statusFilter } : {};
            const { data } = await api.get("/enquiries", { params });
            setItems(data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }
    useEffect(() => { load(); }, [statusFilter]);

    async function setStatus(eid, next) {
        try {
            await api.patch(`/enquiries/${eid}/status`, { status: next });
            setItems((prev) => prev.map((x) => (x.id === eid ? { ...x, status: next } : x)));
            toast.success(`Marked as ${next}`);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    const term = q.trim().toLowerCase();
    const visible = term
        ? items.filter((x) =>
              [x.name, x.mobile, x.email, x.subject, x.message]
                  .filter(Boolean)
                  .some((f) => f.toLowerCase().includes(term)),
          )
        : items;

    const counts = STATUSES.reduce((acc, s) => {
        acc[s] = items.filter((x) => x.status === s).length;
        return acc;
    }, {});

    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
                        Contact Enquiries
                    </h1>
                    <p className="text-sm text-slate-500">
                        Public &ldquo;Send us a Message&rdquo; submissions.
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={load}
                    disabled={busy}
                    data-testid="enq-refresh"
                    className="h-10 rounded-xl"
                >
                    <RefreshCcw className="h-4 w-4 mr-1" />
                    Refresh
                </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Total" value={items.length} onClick={() => setStatusFilter("all")}
                    active={statusFilter === "all"} testid="enq-stat-all" />
                {STATUSES.map((s) => (
                    <StatCard
                        key={s}
                        label={s}
                        value={counts[s] || 0}
                        onClick={() => setStatusFilter(s)}
                        active={statusFilter === s}
                        testid={`enq-stat-${s.toLowerCase()}`}
                    />
                ))}
            </div>

            <div className="flex flex-wrap gap-2">
                <Input
                    placeholder="Search name / mobile / email / message…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="h-10 rounded-xl max-w-sm"
                    data-testid="enq-search"
                />
            </div>

            <Card className="rounded-2xl border-slate-100 p-0 overflow-hidden">
                {visible.length === 0 ? (
                    <div className="py-14 text-center text-sm text-slate-400">
                        {busy ? "Loading…" : "No enquiries yet."}
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {visible.map((e) => (
                            <div
                                key={e.id}
                                className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
                                data-testid={`enq-row-${e.id}`}
                            >
                                <div className="min-w-0 flex-1 space-y-1.5">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="font-semibold text-slate-900">{e.name}</div>
                                        <Badge className={STATUS_STYLE[e.status] || "bg-slate-100 text-slate-700"}>
                                            {e.status}
                                        </Badge>
                                        <span className="text-xs text-slate-500">
                                            {fmtDate(e.created_at)}
                                        </span>
                                    </div>
                                    <div className="text-xs text-slate-600 flex flex-wrap gap-3">
                                        <a
                                            href={`tel:${e.mobile}`}
                                            className="inline-flex items-center gap-1 hover:text-blue-900"
                                        >
                                            <Phone className="h-3.5 w-3.5" /> {e.mobile}
                                        </a>
                                        {e.email && (
                                            <a
                                                href={`mailto:${e.email}`}
                                                className="inline-flex items-center gap-1 hover:text-blue-900"
                                            >
                                                <Mail className="h-3.5 w-3.5" /> {e.email}
                                            </a>
                                        )}
                                    </div>
                                    {e.subject && (
                                        <div className="text-sm font-medium text-slate-800">
                                            {e.subject}
                                        </div>
                                    )}
                                    <div className="text-sm text-slate-700 leading-relaxed flex gap-2">
                                        <MessageSquare className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                                        <span className="whitespace-pre-wrap">{e.message}</span>
                                    </div>
                                </div>
                                <div className="shrink-0">
                                    <Select
                                        value={e.status}
                                        onValueChange={(v) => setStatus(e.id, v)}
                                    >
                                        <SelectTrigger
                                            className="h-9 rounded-xl w-36"
                                            data-testid={`enq-status-${e.id}`}
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {STATUSES.map((s) => (
                                                <SelectItem key={s} value={s}>{s}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}

function StatCard({ label, value, onClick, active, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`rounded-2xl border p-3 text-left transition ${
                active
                    ? "bg-blue-900 text-white border-blue-900"
                    : "bg-white border-slate-100 hover:border-slate-200 text-slate-900"
            }`}
        >
            <div className={`text-[11px] uppercase tracking-wider font-semibold ${
                active ? "text-blue-100" : "text-slate-500"
            }`}>{label}</div>
            <div className="text-xl font-bold mt-0.5">{value}</div>
        </button>
    );
}
